<script setup lang="ts">
import type { MetaSEO } from 'types/seo'

import type { StoryCategory } from 'types/storyblok/category-page'
import type { QueryObject } from 'ufo'
import type { CategoryType, FilterGroup } from '~/types/models/filter.model'
import { callWithNuxt } from '#app'
import consola from 'consola'
import { getMainStory } from 'lib/content/getMainStory'
import {
  generateAlternateLinks,
  generateCanonicalLink,
  getBaseUrl,
  getPrimaryMeta,
  getSEODataFromStoryCategory,
  getSEODataFromStoryPage,
  getSeoHeadData,
  getSeoTitle,
} from 'lib/seo'
import { getStoryApiUrlParams } from 'lib/storyblok'
import { capitalize } from 'lib/strings'

import { isStoryCategory, normalizeCategory } from 'types/storyblok/category-page'
import { withQuery } from 'ufo'

import { usePageTypeSchema } from '~/composables/usePageTypeSchema'
import { generateSEOLinksFromPage } from '~/lib/routing'
import { stripLanguageCodeFromUrl } from '~/lib/url'
import { isStoryLandingPage } from '~/types/storyblok/pages/landing-page'
import StoryblokCategory from '../../../storyblok/Category.vue'

const logger = useServerLogging('page_catchall')

definePageMeta({
  context: 'storyblok',
  section: 'storyblok',
  validate({ params }) {
    const blockedSlugs = [
      'undefined',
      '.well-known',
      '_nuxt',
      'sitemap.xml',
      'robots.txt',
      'favicon.ico',
      'favicon.png',
      'sitemap',
      'api',
      'checkout/undefined',
    ]

    const paramValues = Object.values(params)

    if (params.storefront && typeof params.storefront === 'string' && blockedSlugs.some(path => paramValues.includes(path))) {
      consola.info(`Blocked route ${paramValues.join('/')}`)

      return false
    }

    return true
  },
})

const {
  public: { baseUrlClient },
} = useRuntimeConfig()
const nuxtApp = useNuxtApp()

const route = useRoute()
const pathWithoutLanguageCodeAndWithoutPage = stripLanguageCodeFromUrl(route.path)

const path = pathWithoutLanguageCodeAndWithoutPage
const fullPath = withQuery(pathWithoutLanguageCodeAndWithoutPage, route.query)

const storefrontStore = useStorefrontStore()
const storyblokStore = useStoryblokStore()
const facetStore = useFacetStore()
const filterStore = useFilterStore()

const { sortedBy } = useProductSortingOptions()

const categoryId = ref<string | undefined>(undefined)
const categoryTag = ref('')
const categoryType = ref<CategoryType | undefined>(undefined)
const parentCategory = computed(() =>
  typeof route.params.slugs === 'object'
    ? route.params.slugs?.slice(0, -1).map(capitalize).join(' / ') || ''
    : '',
)

const SEO = ref<MetaSEO | null>(null)

// Pagination
const pages = ref(1)

const { page, isEmpty, prevPageUrl, nextPageUrl } = usePaginationPLP({
  pages,
})

if (page.value < 1) {
  throw createError({
    statusCode: 404,
    message: `Page ${page.value} not found for category ${categoryTag.value}`,
  })
}

const { language } = useLanguage()

if (route.params.slugs?.[0] === 'undefined' || route.params.slugs?.[0] === 'search') {
  throw createError({
    statusCode: 404,
    message: 'Route path is empty',
  })
}

// START Fetch Story
const { contentKey, currentStorefrontCode, allMarkets } = storeToRefs(storefrontStore)
const storyRequestsKey = computed(() => `story-${path}`)

const { storyblokContentVersion } = useStoryblokMode()

const storyblokApi = useStoryblokApi()
const storyblokUrlParams = getStoryApiUrlParams(fullPath, {
  contentKey: contentKey.value,
  language,
  version: storyblokContentVersion.value,
  resolveRelations: ['category.product_list_media_cards', 'category.quick_filters'],
})

const { data: story, error } = await useAsyncData(
  storyRequestsKey.value,
  async () => {
    const { data } = await storyblokApi.get(`cdn/stories`, { ...storyblokUrlParams })
    const story = getMainStory(data.stories, contentKey.value, currentStorefrontCode.value)

    if (!story) {
      logger.logWarning({
        message: 'Catchall page: Storyblok data not found',
        code: 'storyblok_fetch_404',
        data: JSON.stringify({
          route,
          path,
          request: withQuery(
            'https://api.storyblok.com/v2/cdn/stories',
            storyblokUrlParams as QueryObject,
          ),
        }),
      })
      throw createError({
        statusCode: 404,
        message: `Storyblok data not found for ${path} and request ${withQuery(
          'https://api.storyblok.com/v2/cdn/stories',
          storyblokUrlParams as QueryObject,
        )}`,
      })
    }

    return story
  },
  {
    deep: false,
    getCachedData: (key) => {
      return nuxtApp.payload.data?.[key]
    },
  },
)

if (error.value)
  console.error('Storyblok data not found', error.value)

if (error.value && !story.value) {
  console.error('Storyblok data not found', error.value)
  throw createError({
    statusCode: 404,
    message: error.value?.message || 'Storyblok data not found',
  })
}

async function setCategory(story: StoryCategory) {
  const category = normalizeCategory(story)
  filterStore.setFilterRules(category.filterRules)
  if (category.xoTag) {
    categoryType.value = 'tag'
    categoryId.value = category.xoTag
    categoryTag.value = category.xoTag
  }
  else if (category.id) {
    categoryType.value = 'category'
    categoryId.value = category.id
    categoryTag.value = `category-${category.id}`
  }

  facetStore.setCategory(categoryId.value, categoryType.value)
  SEO.value = await callWithNuxt(nuxtApp, getSEODataFromStoryCategory, [story])
}

function unsetCategory() {
  filterStore.setFilterRules([])
  facetStore.setCategory(undefined, undefined)
}

if (story.value && isStoryLandingPage(story.value))
  SEO.value = getSEODataFromStoryPage(story.value)

if (story.value && isStoryCategory(story.value))
  setCategory(story.value)

const widgetId = computed(() => {
  const widgetStoryReference = story.value?.content?.widget
  return widgetStoryReference ? storyblokStore.state.widgets?.get(widgetStoryReference) : undefined
})
// END Fetch Story

// Fetch all selectable Facets for the current category
const { data: allFacetsForCategory } = useAsyncData(
  `all-facets-${categoryTag.value}`,
  async () => {
    if (!categoryId.value || !categoryType.value)
      return Promise.resolve(undefined)

    const data = await facetStore.fetchAllAvailableFiltersForCategory(
      categoryId.value,
      categoryType.value,
    )

    return data.facets
  },
  {
    watch: [categoryTag],
    deep: false,
    getCachedData: (key) => {
      return nuxtApp.payload.data?.[key]
    },
  },
)

watch(
  allFacetsForCategory,
  (data: FilterGroup[]) => {
    if (!data)
      return
    filterStore.setAllFacets(data)
  },
  { immediate: true },
)
// END Fetch all selectable Facets for the current category

// START Fetch Products
const { data: dataProducts, pending: fetchingProducts } = await useProducts({
  widgetId,
  categoryTag,
  page,
  pages,
  sort: sortedBy,
})
const productLoading = computed(() => dataProducts.value.products === undefined && fetchingProducts.value)
const isfetchingProducts = computed(() => fetchingProducts.value)
// END Fetch Products

onMounted(() => {
  if (story.value && story.value.id)
    useStoryblokBridge(story.value?.id, evStory => (story.value = evStory as any))

  if (story.value && isStoryCategory(story.value))
    setCategory(story.value)
})

onBeforeUnmount(() => {
  unsetCategory()
})

const canonicalLink = computed(() => {
  return generateCanonicalLink({
    baseUrlClient,
    route,
  })
})

const canonicalLinkArray = computed(() => {
  return canonicalLink.value ? [canonicalLink.value] : []
})

const seoLinks = computed(() => [
  ...generateAlternateLinks({
    allMarkets: allMarkets.value,
    fullPath: path,
    baseUrlClient,
  }),
  ...generateSEOLinksFromPage({
    currentPage: page.value,
    totalPages: pages.value,
    baseUrl: getBaseUrl({
      baseUrlClient,
    }),
    pathWithoutPage: route.path,
  }),
  ...canonicalLinkArray.value,
  {
    rel: 'icon',
    type: 'image/png',
    href: '/favicon.png',
  },
])

useSeoMeta(getSeoHeadData(SEO.value, currentStorefrontCode.value, { route }))

const { schema } = usePageTypeSchema({ story: story.value, canonical: canonicalLink?.value?.href ?? '' })
const jsonPageTypeSchema = JSON.stringify(schema)

useHead({
  title: () => getSeoTitle(SEO.value, currentStorefrontCode.value, { route }),
  meta: () => (SEO.value && getPrimaryMeta(SEO.value.title, SEO.value.description, SEO.value.meta_robots)) || [],
  bodyAttrs: {
    class: path.includes('/faq') ? 'faq-page' : '',
  },
  link: seoLinks,
  script: [
    {
      innerHTML: jsonPageTypeSchema,
      type: 'application/ld+json',
    },
  ],
})
</script>

<template>
  <div
    v-if="story"
    id="storyblok-page"
    data-test="storyblok-category-page"
  >
    <div v-if="isStoryCategory(story)">
      <StoryblokCategory
        :blok="story.content"
        :products="dataProducts.products"
        :loading="productLoading"
        :fetching-products="isfetchingProducts"
        :parent-category="parentCategory"
        :page="page"
        :pages="pages"
        :is-empty="isEmpty"
        :prev-page-url="prevPageUrl"
        :next-page-url="nextPageUrl"
        :total-products="dataProducts.total ? dataProducts.total : 0"
        data-test="storyblok-category"
      />
    </div>
    <div
      v-else-if="story?.content?.main"
      id="storyblok-content"
      class="container"
    >
      <StoryblokNavPageSubmenu v-if="storyblokStore.hasStorySubmenu(path)" />
      <component
        :is="item.component"
        v-for="(item, index) in story.content.main"
        :key="item._uid"
        :name="item.component"
        :story="item"
        :blok="item"
        :position="index"
      />
    </div>
    <div v-else>
      <component
        :is="story.content.component"
        v-if="story"
        :key="story.content._uid"
        :blok="story.content"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  & > :first-child {
    padding-top: 7.8rem;
  }
}
</style>
